import axios from 'axios';

const API_URL = 'https://api.ironwall.in/api';

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
});

const publicApi = axios.create({
  baseURL: API_URL,
  timeout: 30000,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('ironwall_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('ironwall_token');
      localStorage.removeItem('ironwall_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  adminLogin: (data) => api.post('/auth/admin/login', data),
  vendorLogin: (data) => api.post('/auth/vendor/login', data),
  vendorRegister: (data) => api.post('/auth/vendor/register', data),
  verifyOtp: (data) => api.post('/auth/vendor/verify-otp', data),
  resendOtp: (data) => api.post('/auth/vendor/resend-otp', data),
  getProfile: () => api.get('/auth/profile'),
  changePassword: (data) => api.post('/auth/change-password', data),
  customerLogin: (data) => api.post('/customer-auth/login', data),
  customerRegister: (data) => api.post('/customer-auth/register', data),
  customerVerifyOtp: (data) => api.post('/customer-auth/verify-otp', data),
  customerResendOtp: (data) => api.post('/customer-auth/resend-otp', data),
  getCustomerProfile: () => api.get('/customer-auth/profile'),
};

export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getVendors: (params) => api.get('/admin/vendors', { params }),
  getPendingVendors: () => api.get('/admin/vendors/pending'),
  getVendorDetail: (id) => api.get(`/admin/vendors/${id}`),
  approveVendor: (id) => api.patch(`/admin/vendors/${id}/approve`),
  rejectVendor: (id, data) => api.patch(`/admin/vendors/${id}/reject`, data),
  suspendVendor: (id, data) => api.patch(`/admin/vendors/${id}/suspend`, data),
  reactivateVendor: (id) => api.patch(`/admin/vendors/${id}/reactivate`),
  updateCredits: (id, data) => api.patch(`/admin/vendors/${id}/credits`, data),
  getAdmins: () => api.get('/admin/admins'),
  createAdmin: (data) => api.post('/admin/admins', data),
};

export const vendorAPI = {
  getProfile: () => api.get('/vendors/profile'),
  updateProfile: (data) => api.put('/vendors/profile', data),
  getUsers: () => api.get('/vendors/users'),
  addUser: (data) => api.post('/vendors/users', data),
  updateUser: (id, data) => api.put(`/vendors/users/${id}`, data),
  getCategories: () => api.get('/vendors/categories'),
};

export const engineerAPI = {
  getAll: (params) => api.get('/engineers', { params }),
  getOne: (id) => api.get(`/engineers/${id}`),
  add: (data) => api.post('/engineers', data),
  update: (id, data) => api.put(`/engineers/${id}`, data),
  deactivate: (id) => api.patch(`/engineers/${id}/deactivate`),
  delete: (id) => api.delete(`/engineers/${id}`),
  resetPassword: (id, data) => api.patch(`/engineers/${id}/reset-password`, data),
  getLiveLocations: () => api.get('/engineers/live-locations'),
};

export const customerAPI = {
  getAll: (params) => api.get('/customers', { params }),
  getOne: (id) => api.get(`/customers/${id}`),
  add: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  addAddress: (id, data) => api.post(`/customers/${id}/addresses`, data),
  updateAddress: (addressId, data) => api.put(`/customers/addresses/${addressId}`, data),
  deleteAddress: (addressId) => api.delete(`/customers/addresses/${addressId}`),
  delete: (id) => api.delete(`/customers/${id}`),
};

export const inventoryAPI = {
  getAll: (params) => api.get('/inventory', { params }),
  getOne: (id) => api.get(`/inventory/${id}`),
  add: (data) => api.post('/inventory', data),
  update: (id, data) => api.put(`/inventory/${id}`, data),
  delete: (id) => api.delete(`/inventory/${id}`),
  getSummary: () => api.get('/inventory/summary'),
  getCatalog: (params) => api.get('/catalog', { params }),
  downloadTemplate: () => api.get('/inventory/bulk-upload/template', { responseType: 'blob' }),
  bulkUpload: (formData) => api.post('/inventory/bulk-upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

export const ticketAPI = {
  getAll: (params) => api.get('/tickets', { params }),
  getOne: (id) => api.get(`/tickets/${id}`),
  create: (data) => api.post('/tickets', data),
  assign: (id, data) => api.patch(`/tickets/${id}/assign`, data),
  updateStatus: (id, data) => api.patch(`/tickets/${id}/status`, data),
  close: (id, data) => api.patch(`/tickets/${id}/close`, data),
  getProof: (id) => api.get(`/proofs/ticket/${id}`),
  getVisitNotes: (id) => api.get(`/visit-notes/ticket/${id}`),
  assignFreelancer: (id, data) => api.post(`/tickets/${id}/assign-freelancer`, data),
  regenerateFreelancerLink: (id, data) => api.post(`/tickets/${id}/regenerate-link`, data),
  revokeFreelancerLink: (id) => api.post(`/tickets/${id}/revoke-link`),
};

export const freelancerAPI = {
  getAll: (params) => api.get('/freelancers', { params }),
  add: (data) => api.post('/freelancers', data),
  update: (id, data) => api.put(`/freelancers/${id}`, data),
  delete: (id) => api.delete(`/freelancers/${id}`),
};

export const requirementAPI = {
  getAll: (params) => api.get('/requirements', { params }),
  getOne: (id) => api.get(`/requirements/${id}`),
  create: (formData) => api.post('/requirements', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  updateStatus: (id, data) => api.patch(`/requirements/${id}/status`, data),
  respond: (id, data) => api.post(`/requirements/${id}/respond`, data),
  updateResponseStatus: (id, responseId, data) => api.patch(`/requirements/${id}/responses/${responseId}`, data),
};

export const reportsAPI = {
  getDashboard: () => api.get('/reports/dashboard'),
  getDeliveries: (params) => api.get('/reports/deliveries', { params }),
  getEngineers: (params) => api.get('/reports/engineers', { params }),
  getInventory: () => api.get('/reports/inventory'),
  getSLA: (params) => api.get('/reports/sla', { params }),
  getCustomers: () => api.get('/reports/customers'),
  exportPDF: (endpoint, params) => api.get(`/reports/${endpoint}`, {
    params: { ...params, format: 'pdf' },
    responseType: 'blob',
  }),
};

export const locationAPI = {
  getLive: () => api.get('/location/live'),
  getHistory: (engineerId, params) => api.get(`/location/history/${engineerId}`, { params }),
  getActivitySummary: (params) => api.get('/location/activity-summary', { params }),
  getEngineerActivity: (engineerId, params) => api.get(`/location/activity/${engineerId}`, { params }),
};

export const freelancerTaskAPI = {
  verify: (token, phone) => publicApi.post(`/public/task/${token}/verify`, { phone }),
  getTicket: (token, phone) => publicApi.get(`/public/task/${token}`, { params: { phone } }),
  createProof: (token, data) => publicApi.post(`/public/task/${token}/proof`, data),
  uploadImage: (token, proofId, formData) => publicApi.post(`/public/task/${token}/proof/${proofId}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  uploadSignature: (token, proofId, formData) => publicApi.post(`/public/task/${token}/proof/${proofId}/signature`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  submitVisitNote: (token, formData) => publicApi.post(`/public/task/${token}/visit-note`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

export default api;
